import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PresignedUpload } from '@foundation/shared';
import { ApiRequestError, api, tokenStore } from '../lib/api';
import { sha256Hex, shrinkImage } from '../lib/image';
import { bahtTextToSatang, localInputToIso, nowLocalInput } from '../lib/format';
import { useDonor } from '../auth';

interface PresignResponse {
  uploads: PresignedUpload[];
  mode: 'presigned' | 'worker';
}

export default function ReportSlip() {
  const donor = useDonor();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [amountText, setAmountText] = useState(() => {
    const s = params.get('amount');
    return s ? String(Number(s) / 100) : '';
  });
  const [donatedAt, setDonatedAt] = useState(nowLocalInput());
  const [payerName, setPayerName] = useState(donor.displayName);
  const [slipRef, setSlipRef] = useState('');
  const [wantsReceipt, setWantsReceipt] = useState(false);
  const [receiptName, setReceiptName] = useState(donor.displayName);
  const [receiptTaxId, setReceiptTaxId] = useState('');
  const [receiptAddress, setReceiptAddress] = useState('');

  const [slip, setSlip] = useState<{ key: string; sha256: string; previewUrl: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const satang = bahtTextToSatang(amountText);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    setError(null);
    setDuplicate(null);
    try {
      const image = await shrinkImage(file);
      const hash = await sha256Hex(image.blob);

      const presign = await api<PresignResponse>('/uploads/presign', {
        method: 'POST',
        body: { purpose: 'slip', refId: donor.id, count: 1, contentType: 'image/jpeg' },
      });
      const target = presign.uploads[0];
      if (!target) throw new Error('ขอที่อยู่สำหรับอัปโหลดไม่สำเร็จ');

      const headers: Record<string, string> = { 'content-type': 'image/jpeg' };
      if (presign.mode === 'worker') {
        const token = tokenStore.get();
        if (token) headers.authorization = `Bearer ${token}`;
      }

      const res = await fetch(target.url, { method: 'PUT', headers, body: image.blob });
      if (!res.ok) throw new Error(`อัปโหลดสลิปไม่สำเร็จ (${res.status})`);

      setSlip({ key: target.key, sha256: hash, previewUrl: image.previewUrl });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'อัปโหลดสลิปไม่สำเร็จ');
    } finally {
      setUploading(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDuplicate(null);

    if (!slip) {
      setError('กรุณาแนบรูปสลิปก่อน');
      return;
    }
    if (!satang) {
      setError('กรุณาระบุจำนวนเงินให้ถูกต้อง');
      return;
    }

    setBusy(true);
    try {
      const res = await api<{ donationNo: string; message: string }>('/donations/report', {
        method: 'POST',
        idempotent: true,
        body: {
          amount: satang,
          donatedAt: localInputToIso(donatedAt),
          channel: 'promptpay',
          campaignId: params.get('campaignId') || undefined,
          bankAccountId: params.get('bankAccountId') || undefined,
          slipKey: slip.key,
          slipSha256: slip.sha256,
          slipRef: slipRef.trim() || undefined,
          payerName: payerName.trim() || undefined,
          wantsReceipt,
          receipt: wantsReceipt
            ? {
                name: receiptName.trim(),
                taxId: receiptTaxId.trim() || undefined,
                address: receiptAddress.trim() || undefined,
              }
            : undefined,
        },
      });
      navigate(`/done?no=${encodeURIComponent(res.donationNo)}`);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        const details = err.details as { existingDonationNo?: string } | undefined;
        if (details?.existingDonationNo) {
          setDuplicate(details.existingDonationNo);
        } else {
          setError(err.message);
        }
      } else {
        setError('บันทึกไม่สำเร็จ กรุณาลองใหม่');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => void onPickFile(e)}
      />

      <div className="topbar">
        <button className="back" onClick={() => navigate(-1)}>← กลับ</button>
        <h1>แจ้งโอน</h1>
      </div>

      {error && <div className="alert error">{error}</div>}
      {duplicate && (
        <div className="alert warn">
          สลิปนี้เคยแจ้งเข้ามาแล้ว (เลขที่ {duplicate}) ไม่ต้องส่งซ้ำนะคะ 🙏
          <br />
          <button
            type="button"
            className="secondary block"
            style={{ marginTop: 10 }}
            onClick={() => navigate('/history')}
          >
            ดูประวัติการบริจาค
          </button>
        </div>
      )}

      <form onSubmit={submit}>
        <div className="card">
          <h2>รูปสลิป *</h2>
          {slip ? (
            <div style={{ display: 'grid', placeItems: 'center', gap: 10 }}>
              <img
                src={slip.previewUrl}
                alt="สลิปที่แนบ"
                style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 10 }}
              />
              <button type="button" className="secondary block" onClick={() => fileRef.current?.click()}>
                เปลี่ยนรูป
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="secondary block"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? 'กำลังอัปโหลด…' : '📷 เลือกรูปสลิป'}
            </button>
          )}
          <p className="muted small" style={{ marginBottom: 0 }}>
            ระบบย่อรูปให้อัตโนมัติ ส่งได้แม้สัญญาณไม่ดี
          </p>
        </div>

        <div className="card">
          <h2>รายละเอียดการโอน</h2>
          <div className="field">
            <label htmlFor="amt">จำนวนเงิน (บาท) *</label>
            <input
              id="amt"
              type="text"
              inputMode="decimal"
              required
              value={amountText}
              onChange={(e) => setAmountText(e.target.value)}
            />
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              เจ้าหน้าที่จะตรวจสอบกับสลิปอีกครั้ง หากไม่ตรงจะยึดตามยอดที่เข้าบัญชีจริง
            </p>
          </div>
          <div className="field">
            <label htmlFor="at">วันเวลาที่โอน *</label>
            <input
              id="at"
              type="datetime-local"
              required
              value={donatedAt}
              onChange={(e) => setDonatedAt(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="payer">ชื่อผู้โอน</label>
            <input id="payer" value={payerName} onChange={(e) => setPayerName(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ref">เลขที่อ้างอิงบนสลิป</label>
            <input
              id="ref"
              value={slipRef}
              onChange={(e) => setSlipRef(e.target.value)}
              placeholder="ไม่บังคับ — ช่วยให้ตรวจสอบเร็วขึ้น"
            />
          </div>
        </div>

        <div className="card">
          <label className="checkbox" style={{ marginBottom: wantsReceipt ? 14 : 0 }}>
            <input
              type="checkbox"
              checked={wantsReceipt}
              onChange={(e) => setWantsReceipt(e.target.checked)}
            />
            <span>ต้องการใบอนุโมทนาบัตร</span>
          </label>

          {wantsReceipt && (
            <>
              <div className="field">
                <label htmlFor="rname">ชื่อบนใบอนุโมทนาบัตร *</label>
                <input
                  id="rname"
                  required={wantsReceipt}
                  value={receiptName}
                  onChange={(e) => setReceiptName(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="rtax">เลขประจำตัวผู้เสียภาษี</label>
                <input
                  id="rtax"
                  inputMode="numeric"
                  maxLength={17}
                  value={receiptTaxId}
                  onChange={(e) => setReceiptTaxId(e.target.value)}
                  placeholder="สำหรับลดหย่อนภาษี"
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="raddr">ที่อยู่</label>
                <input
                  id="raddr"
                  value={receiptAddress}
                  onChange={(e) => setReceiptAddress(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <button type="submit" className="block" disabled={busy || uploading || !slip}>
          {busy ? 'กำลังส่ง…' : 'ส่งข้อมูลแจ้งโอน'}
        </button>
      </form>
    </div>
  );
}
